import React from 'react';

import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  HTMLProps,
  MouseEventHandler,
  ReactNode,
  Ref,
} from 'react';

import { IconButtonVariantEnum } from '../../types/variants';
import cx from '../../utils/classNames';
import toCamelCase from '../../utils/toCamelCase';

import styles from './iconButton.module.scss';

/** Custom props */
type BaseProps<T extends string | undefined> = {
  /** The icon content of the component */
  children?: ReactNode;
  /** Custom class to overwrite default styling if needed */
  className?: string;
  /** React Ref for the button element */
  innerRef?: T extends string ? Ref<HTMLAnchorElement> : Ref<HTMLButtonElement>;
  /** When true, makes the element not mutable, focusable, or submitted with the form */
  isDisabled?: boolean;
  /** Calls a function when the button is clicked */
  onClick?: T extends string
    ? MouseEventHandler<HTMLAnchorElement>
    : MouseEventHandler<HTMLButtonElement>;
  /** Define the default behavior of the button */
  type?: 'submit' | 'reset' | 'button';
  /** The visual variant of the button */
  variant?: IconButtonVariantEnum;
  /** The shape of the button */
  shape?: 'round' | 'square';
  /** aria-label for accessibility */
  'aria-label': string;
};

export type Props<T extends string | undefined> = T extends string
  ? BaseProps<string> & {
      /**
       * The URL to link to when the button is clicked.
       * If defined, an a element will be used as the root node.
       */
      href: T;
    } & Omit<HTMLProps<HTMLAnchorElement>, 'href' | 'size' | 'ref'>
  : BaseProps<undefined> & { href?: T } & Omit<
        HTMLProps<HTMLButtonElement>,
        'href' | 'size' | 'ref' | 'type'
      >;

export function IconButton(props: Props<string | undefined>) {
  const {
    children,
    className,
    href,
    innerRef,
    isDisabled,
    onClick,
    shape = 'round',
    type = 'button',
    variant = IconButtonVariantEnum.Filled,
    ...rest
  } = props;

  const classNames = cx(
    styles.iconButton,
    styles[toCamelCase('iconButton', variant)],
    shape === 'square' ? styles[toCamelCase('iconButton', 'square')] : '',
    isDisabled ? styles[toCamelCase('iconButton', 'disabled')] : '',
    className,
  );

  if (href !== undefined) {
    return (
      <a
        className={classNames}
        href={href}
        onClick={onClick}
        ref={innerRef}
        {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {children}
      </a>
    );
  } else {
    return (
      <button
        className={classNames}
        disabled={isDisabled}
        type={type}
        onClick={onClick}
        ref={innerRef}
        {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {children}
      </button>
    );
  }
}

export default IconButton;
